import { useCallback, useMemo } from 'react'
import {
  BacktestStatusPill,
  ChartConfigBar,
  ChartPane,
  ChartReplayBar,
} from '@/components'
import type { BacktestStatus, Candle } from '@/components'
import GraphCandle, { type CandleData, type GraphCandleHandle, type OrderData } from './GraphCandle'
import BacktestConfigGroups from './BacktestConfigGroups'
import type { BacktestResult } from '../api'
import { AVAILABLE_SPEEDS } from '../hooks/useBacktestChartState'

const TIMEFRAMES = ['M1', 'H1', 'D1'] as const

// Resolution of the synthetic index space we expose to ChartReplayBar.
// The bar is index-based, but our chart state is time-based with windowed
// loading, so we project our [rangeStartTs, rangeEndTs] range onto a fixed
// number of "scrub buckets" and translate back to a timestamp on each scrub.
const SCRUB_RESOLUTION = 1000

interface BotBacktestTopSectionProps {
  symbol: string
  timeframe: string
  startDate: string
  endDate: string
  speed: number
  isPlaying: boolean
  isPlayDisabled: boolean
  candles: CandleData[]
  orders: OrderData[]
  maxAvailableTs: number | null
  chartRef: React.RefObject<GraphCandleHandle>
  configInput: BacktestResult['configInput']
  onVisibleRangeChange: (range: { from: number; to: number }) => void
  onUserNavigate: () => void
  onReset: () => void
  onPlay: () => void
  onPause: () => void
  onTimeframeChange: (tf: string) => void
  onSpeedChange: (speed: number) => void
  onCycleSpeed: () => void
  onScrubTime: (timestamp: number) => void
  isInProgress: boolean
  progressPercent: number
  progressViewPosition: number
  rangeStartTs: number
  rangeEndTs: number
  backtestStartedAtMs: number | null
  playStartedAtMs: number | null
  playElapsedSec: number
  progressViewTs: number
}

export default function BotBacktestTopSection({
  symbol,
  timeframe,
  startDate,
  endDate,
  speed,
  isPlaying,
  isPlayDisabled,
  candles,
  orders,
  maxAvailableTs,
  chartRef,
  configInput,
  onVisibleRangeChange,
  onUserNavigate,
  onReset,
  onPlay,
  onPause,
  onTimeframeChange,
  onSpeedChange,
  onCycleSpeed,
  onScrubTime,
  isInProgress,
  progressPercent,
  progressViewPosition,
  rangeStartTs,
  rangeEndTs,
  backtestStartedAtMs,
  playStartedAtMs,
  playElapsedSec,
  progressViewTs,
}: BotBacktestTopSectionProps) {
  const rangeTotal = rangeEndTs - rangeStartTs

  const scrubOhlc = useMemo<Candle[]>(
    () =>
      Array.from({ length: SCRUB_RESOLUTION }, (_, i) => ({
        time: rangeStartTs + (i / SCRUB_RESOLUTION) * rangeTotal,
        open: 0,
        high: 0,
        low: 0,
        close: 0,
      })),
    [rangeStartTs, rangeTotal],
  )

  // Thumb position is always the replay (progressView) position. Chart-center
  // panning is a separate concern and must not move the replay thumb.
  const replayVisibleCount = Math.max(
    1,
    Math.min(SCRUB_RESOLUTION, Math.round(progressViewPosition * SCRUB_RESOLUTION)),
  )

  // Cap scrubbing at the buffered boundary (maxAvailableTs) so the user can
  // never aim the replay past data the backtest has actually produced.
  const maxScrubIndex = useMemo(() => {
    if (maxAvailableTs == null) return SCRUB_RESOLUTION
    const cappedTs = Math.min(maxAvailableTs, rangeEndTs)
    const fraction = Math.max(0, Math.min(1, (cappedTs - rangeStartTs) / rangeTotal))
    return Math.max(1, Math.round(fraction * SCRUB_RESOLUTION))
  }, [maxAvailableTs, rangeStartTs, rangeEndTs, rangeTotal])

  const handleScrub = useCallback(
    (idx: number) => {
      const cappedIdx = Math.min(idx, maxScrubIndex)
      const fraction = Math.max(0, Math.min(1, cappedIdx / SCRUB_RESOLUTION))
      const targetTs = rangeStartTs + fraction * rangeTotal
      onScrubTime(targetTs)
    },
    [onScrubTime, rangeStartTs, rangeTotal, maxScrubIndex],
  )

  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      onPause()
      return
    }
    if (isPlayDisabled) return
    onPlay()
  }, [isPlaying, isPlayDisabled, onPlay, onPause])

  const pill = useMemo<{
    status: BacktestStatus
    startedAt: number | null
    elapsed: number
  }>(() => {
    if (isInProgress) {
      // "starting" = backtest is running but no candles have been produced yet.
      // Once maxAvailableTs advances past rangeStartTs, switch to live "running" with elapsed
      // since backtest.created.
      const hasAnyCandles = maxAvailableTs !== null && maxAvailableTs > rangeStartTs
      if (!hasAnyCandles) {
        return { status: 'starting', startedAt: null, elapsed: 0 }
      }
      return { status: 'running', startedAt: backtestStartedAtMs ?? null, elapsed: 0 }
    }

    if (isPlaying) {
      return { status: 'replaying', startedAt: playStartedAtMs, elapsed: playElapsedSec }
    }

    if (progressViewTs >= rangeEndTs) {
      return { status: 'complete', startedAt: null, elapsed: 0 }
    }

    return { status: 'paused', startedAt: null, elapsed: playElapsedSec }
  }, [
    isInProgress,
    isPlaying,
    maxAvailableTs,
    rangeStartTs,
    rangeEndTs,
    backtestStartedAtMs,
    playStartedAtMs,
    playElapsedSec,
    progressViewTs,
  ])

  const configBar = (
    <ChartConfigBar
      symbol={symbol}
      timeframe={timeframe}
      onTimeframeChange={onTimeframeChange}
      timeframes={TIMEFRAMES}
      dateFrom={startDate}
      dateTo={endDate}
      extraConfigSlot={<BacktestConfigGroups configInput={configInput} />}
      statusSlot={
        <BacktestStatusPill
          status={pill.status}
          startedAt={pill.startedAt}
          elapsed={pill.elapsed}
        />
      }
    />
  )

  const chartSlot = (
    <GraphCandle
      ref={chartRef}
      candles={candles}
      orders={orders}
      symbol={symbol}
      timeframe={timeframe}
      fixEdges={false}
      autoSetInitialVisibleRange={false}
      maxAvailableTimestamp={maxAvailableTs ?? undefined}
      onVisibleRangeChange={onVisibleRangeChange}
      onUserNavigate={onUserNavigate}
    />
  )

  const replayBar = (
    <ChartReplayBar
      ohlc={scrubOhlc}
      playing={isPlaying}
      speed={speed}
      visibleCount={replayVisibleCount}
      totalCandles={SCRUB_RESOLUTION}
      maxScrubIndex={maxScrubIndex}
      bufferPct={progressPercent}
      backtestRunning={isInProgress}
      onRestart={onReset}
      onTogglePlay={handleTogglePlay}
      onCycleSpeed={onCycleSpeed}
      onSpeedChange={onSpeedChange}
      onScrub={handleScrub}
      speeds={AVAILABLE_SPEEDS}
    />
  )

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <ChartPane
        configBarSlot={configBar}
        chartSlot={chartSlot}
        replayBarSlot={replayBar}
      />
    </div>
  )
}
